import { useEffect, useMemo, useState } from "react";
import { useOthers, useSelf } from "@liveblocks/react";
import { useStore } from "store";
import { getCachedUser, subscribeUser, ResolvedUserInfo } from "./mentionCache";

export type ResolvedUser = {
  name: string;
  avatar?: string;
  color?: string;
};

// Mirror of the backend's golden-angle HSL algorithm in oppizi-api/src/routes/
// /liveblocks.js. Used as a fallback when a user isn't currently in `useOthers`
// presence (offline collaborators) so two distinct user ids never collide on
// the same default color. Uses a simple FNV-1a hash since we don't ship MD5
// to the browser; the algorithm is deterministic per id, which is all we need
// for a stable visual identity.
const GOLDEN_ANGLE = 137.508;
const fnv1a = (str: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};
const colorFromUserId = (userId: string): string => {
  const hue = Math.round((fnv1a(userId) * GOLDEN_ANGLE) % 360);
  return `hsl(${hue}, 62%, 58%)`;
};

/**
 * Resolve a userId to display info. Priority order:
 *  1. Current user → local zustand store (most accurate source for "me").
 *  2. Our own `/platform/users/resolve` endpoint (source of truth for DB data).
 *  3. Liveblocks presence (useOthers) — fallback for users currently connected
 *     whose auth payload carries fresh userInfo. Lower priority than our API
 *     because Liveblocks may have stale cached info from old sessions.
 *  4. Hardcoded "User" fallback while loading / on error.
 */
export const useLiveblocksUser = (userId: string | undefined): ResolvedUser => {
  const self = useSelf();
  const others = useOthers();
  const storeUser = useStore((s: any) => s.user);

  // Subscribe to the batched API cache for this userId.
  const [apiResolved, setApiResolved] = useState<ResolvedUserInfo | null | undefined>(
    userId ? getCachedUser(userId) : undefined
  );
  useEffect(() => {
    if (!userId) {
      setApiResolved(undefined);
      return;
    }
    // Current user → no need to hit the API, we have local data.
    if (self?.id === userId) {
      setApiResolved(undefined);
      return;
    }
    setApiResolved(getCachedUser(userId));
    const unsub = subscribeUser(userId, () => {
      setApiResolved(getCachedUser(userId));
    });
    return unsub;
  }, [userId, self?.id]);

  return useMemo<ResolvedUser>(() => {
    if (!userId) return { name: "Unknown" };

    // Color is *always* derived from the userId on the frontend so it stays
    // consistent regardless of presence state and immune to stale JWT colors
    // from old backend deploys. This guarantees two distinct user ids never
    // share a color (modulo 1/360 hue collision).
    const color = colorFromUserId(userId);

    // 1. Current user → local zustand store (most accurate)
    if (self?.id === userId) {
      const fullName =
        [storeUser?.firstName, storeUser?.lastName].filter(Boolean).join(" ") ||
        storeUser?.email ||
        (self.info as any)?.name ||
        "You";
      const avatar = storeUser?.profilePicture || (self.info as any)?.avatar;
      return { name: fullName, avatar, color };
    }

    // 2. API-resolved (our backend) — preferred over presence
    if (apiResolved && apiResolved.name) {
      return {
        name: apiResolved.name,
        avatar: apiResolved.avatar || undefined,
        color,
      };
    }

    // 3. Other users currently connected → presence fallback
    const other = others.find((o) => o.id === userId);
    if (other) {
      const info = (other.info as any) || {};
      return {
        name: info.name || "User",
        avatar: info.avatar,
        color,
      };
    }

    // 4. Loading / unresolved
    return { name: "User", color };
  }, [
    userId,
    self?.id,
    (self?.info as any)?.name,
    (self?.info as any)?.avatar,
    (self?.info as any)?.color,
    storeUser?.firstName,
    storeUser?.lastName,
    storeUser?.email,
    storeUser?.profilePicture,
    others,
    apiResolved,
  ]);
};
